import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ModernVoiceConfig = ({ value = {}, onChange, onPreview }) => {
  const [voices, setVoices] = useState({});
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(value.language || '');
  const [selectedVoice, setSelectedVoice] = useState(value.name || '');
  const [pitch, setPitch] = useState(value.pitch !== undefined ? value.pitch : 0);
  const [rate, setRate] = useState(value.rate !== undefined ? value.rate : 1);
  const [previewText, setPreviewText] = useState('This is a preview of the selected Neural voice.');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loadingVoice, setLoadingVoice] = useState(null);

  useEffect(() => {
    const loadVoices = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tts/voices`);
        setVoices(response.data);
        setLanguages(Object.keys(response.data));
        if (!selectedLanguage && Object.keys(response.data).length > 0) {
          setSelectedLanguage(Object.keys(response.data)[0]);
        }
      } catch (err) {
        setError('Failed to load voices. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadVoices();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (onChange) {
      onChange({
        language: selectedLanguage,
        name: selectedVoice,
        pitch,
        rate,
      });
    }
    // eslint-disable-next-line
  }, [selectedLanguage, selectedVoice, pitch, rate]);

  const handleLanguageChange = (e) => {
    setSelectedLanguage(e.target.value);
    setSelectedVoice('');
  };

  const handleVoiceCardClick = (voice) => {
    setSelectedVoice(voice.originalName);
  };

  const handlePlaySample = async (voice) => {
    setLoadingVoice(voice.originalName);
    setPlaying(false);
    try {
      if (!selectedLanguage || !voice.originalName) return;
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/tts/synthesize`,
        {
          text: previewText,
          voice: {
            name: voice.originalName,
            ssmlGender: voice.ssmlGender
          },
          voiceSettings: {
            pitch,
            rate
          }
        },
        { responseType: 'json' }
      );
      if (response.data && response.data.audioContent) {
        const audioBlob = new Blob([Uint8Array.from(atob(response.data.audioContent), c => c.charCodeAt(0))], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        setPlaying(true);
        audio.play();
        audio.onended = () => setPlaying(false);
      }
    } catch (err) {
      // Optionally show error
    } finally {
      setLoadingVoice(null);
    }
  };

  const handlePreview = () => {
    if (onPreview && selectedVoice && selectedLanguage) {
      onPreview({
        language: selectedLanguage,
        name: selectedVoice,
        pitch,
        rate,
        previewText,
      });
    } else {
      // Optionally show error
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-32">Loading voices...</div>;
  }
  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="w-full  mx-auto px-20 p-4">
      {/* Language Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
        <select
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          value={selectedLanguage}
          onChange={handleLanguageChange}
        >
          {languages.map(lang => (
            <option key={lang} value={lang}>{lang}</option>
          ))}
        </select>
      </div>

      {/* Voice Selection */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {voices[selectedLanguage]?.map(voice => (
          <div
            key={voice.originalName}
            className={`p-4 rounded-xl border transition-colors duration-300 cursor-pointer flex flex-col items-start ${
              selectedVoice === voice.originalName ? 'bg-blue-100 border-blue-500 shadow-lg' : 'bg-white border-gray-200 hover:border-blue-300'
            }`}
            onClick={() => handleVoiceCardClick(voice)}
          >
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">{voice.ssmlGender === 'FEMALE' ? '👩' : '👨'}</span>
              <span className="font-semibold text-lg">{voice.name}</span>
            </div>
            <div className="text-gray-600 text-sm mb-2">{voice.description || `${voice.ssmlGender} neural voice`}</div>
            <button
              className="mt-auto px-3 py-1 text-sm rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
              disabled={loadingVoice === voice.originalName}
              onClick={e => { e.stopPropagation(); handlePlaySample(voice); }}
            >
              {loadingVoice === voice.originalName ? 'Loading...' : playing && selectedVoice === voice.originalName ? 'Playing...' : 'Play sample'}
            </button>
          </div>
        ))}
      </div>

      {/* Pitch, Rate, Preview Text */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Pitch: <span className="font-mono">{pitch}</span></label>
        <input
          type="range"
          min={-20}
          max={20}
          value={pitch}
          onChange={e => setPitch(Number(e.target.value))}
          className="w-full"
        />
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Speaking Rate: <span className="font-mono">{rate}</span></label>
        <input
          type="range"
          min={0.25}
          max={4}
          step={0.05}
          value={rate}
          onChange={e => setRate(Number(e.target.value))}
          className="w-full"
        />
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Preview Text</label>
        <textarea
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          rows={2}
          value={previewText}
          onChange={e => setPreviewText(e.target.value)}
        />
      </div>
    </div>
  );
};

export default ModernVoiceConfig; 